import { logger } from '@/utils/logger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  InitializeEmailAuthenticationFlowBody,
  InitializeEmailAuthenticationFlowResponse,
  LoginRequest,
  VerifyEmailAuthenticationFlowBody,
  VerifyEmailAuthenticationFlowResponse,
  WhoamiResponse,
} from '@mai/platform-api/src/routes/authentication.schema';

import { apiClient } from './axios';

// API functions
export const fetchWhoami = async (): Promise<WhoamiResponse> => {
  const response = await apiClient.get('/authentication/whoami');
  return response.data;
};

export const login = async (credentials: LoginRequest): Promise<void> => {
  await apiClient.post('/authentication/login', credentials);
};

export const logout = async (): Promise<void> => {
  await apiClient.post('/authentication/logout');
};

export const initializeEmailAuthenticationFlow = async (
  body: InitializeEmailAuthenticationFlowBody,
): Promise<InitializeEmailAuthenticationFlowResponse> => {
  const response = await apiClient.post(
    '/authentication/email/initialize',
    body,
  );
  return response.data;
};

export const verifyEmailAuthenticationFlow = async (
  body: VerifyEmailAuthenticationFlowBody,
): Promise<VerifyEmailAuthenticationFlowResponse> => {
  const response = await apiClient.post('/authentication/email/verify', body);
  return response.data;
};

// React Query hooks
export const useWhoami = () => {
  return useQuery({
    queryKey: ['whoami'],
    queryFn: fetchWhoami,
    refetchInterval: 60 * 1000, // Refresh every minute
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: async () => {
      logger.info('User logged in successfully');
      // Clear all cached data and refetch whoami
      await queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: ['whoami'] });
    },
    onError: (error) => {
      logger.error('Login failed', { error });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      logger.info('User logged out successfully');
      // Invalidate all queries to force refetch with new auth state
      // This maintains active observers unlike clear()
      await queryClient.invalidateQueries();
    },
    onError: (error) => {
      logger.error('Logout failed', { error });
    },
  });
};

export const useInitializeEmailAuthenticationFlow = () => {
  return useMutation({
    mutationFn: initializeEmailAuthenticationFlow,
    onSuccess: () => {
      logger.info('Magic link email sent successfully');
    },
    onError: (error) => {
      logger.error('Failed to send magic link email', { error });
    },
  });
};

export const useVerifyEmailAuthenticationFlow = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyEmailAuthenticationFlow,
    onSuccess: async () => {
      logger.info('Magic link verification successful');
      // Clear all cached data and refetch whoami
      await queryClient.clear();
      void queryClient.invalidateQueries({ queryKey: ['whoami'] });
    },
    onError: (error) => {
      logger.error('Magic link verification failed', { error });
    },
  });
};
